import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { IntlProvider } from 'react-intl';
import { IntlContext, defaultValues } from 'src/contexts/IntlContext';
import config from 'src/constants';
import { localStore } from 'src/services/storage';
import { postLanguage } from 'src/services/api/company';
import { AppLocale } from 'src/models/modules';
import { lazyTranslations } from 'src/services/lazyTranslations';

const { Provider } = IntlContext;

const IntlProviderWrapper: React.FC = ({ children }) => {
    const languagesWithFormalOption = ['hu', 'de'];
    const { setLocale: _defaultSetLocale, ...initialState } = defaultValues;
    const [state, setState] = useState(initialState);
    const { locale, isFormal, messages } = state;

    const setLocale = (languageCode: AppLocale, isFormalLanguage?: boolean) => {
        setState({
            ...state,
            locale: languageCode,
            isFormal: isFormalLanguage ?? isFormal,
            alreadySwitched: true,
        });
    };
    
    const loadMessages = async () => {
        const hasFormalOption = languagesWithFormalOption.includes(locale);

        const translations = await lazyTranslations[
            (locale +
                (isFormal && hasFormalOption ? '-formal' : '')) as keyof typeof lazyTranslations
        ];

        localStore.setItem('locale', locale);
        localStore.setItem('isFormalLocale', isFormal.toString());
        postLanguage(locale).catch(() => null);
        
        setState((prevState) => ({ ...prevState, messages: translations }));
    };

    useEffect(() => {
        loadMessages();
    }, [locale, isFormal]);

    return (
        <>
            <Helmet>
                <html lang={locale} />
            </Helmet>
            <Provider value={{ ...state, setLocale }}>
                <IntlProvider
                    key={locale}
                    locale={locale}
                    messages={messages}
                    defaultLocale={config.DEFAULT_LOCALE}
                    timeZone={config.SERVER_TIME_ZONE}
                >
                    {children}
                </IntlProvider>
            </Provider>
        </>
    );
};

export { IntlProviderWrapper as IntlProvider };
